import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import clsx from 'classnames'

type Size = 'sm' | 'md' | 'lg' | 'xl'
type Tone = 'default' | 'danger'

export interface AppDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void

  title?: React.ReactNode
  description?: React.ReactNode

  /** Main content */
  children?: React.ReactNode

  /** If provided, replaces the default footer (Cancel / Confirm) */
  footer?: React.ReactNode

  /* Default footer controls */
  onConfirm?: () => void | Promise<void>
  confirmLabel?: string
  confirmDisabled?: boolean
  confirmTone?: Tone
  isBusy?: boolean
  cancelLabel?: string

  /** Prevent closing while busy (overlay click / esc) */
  lockOnBusy?: boolean

  /** Width preset */
  size?: Size

  className?: string
}

const SIZE_CLS: Record<Size, string> = {
  sm: 'w-[380px] max-w-[95vw]',
  md: 'w-[560px] max-w-[95vw]',
  lg: 'w-[720px] max-w-[95vw]',
  xl: 'w-[920px] max-w-[95vw]',
}

export default function AppDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,

  onConfirm,
  confirmLabel = 'Confirm',
  confirmDisabled = false,
  confirmTone = 'default',
  isBusy = false,
  cancelLabel = 'Cancel',
  lockOnBusy = true,

  size = 'md',
  className,
}: AppDialogProps) {
  const blockOutside = lockOnBusy && isBusy

  return (
    <Dialog.Root open={open} onOpenChange={(v) => {
      // don’t allow closing while busy
      if (blockOutside && v === false) return
      onOpenChange(v)
    }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] pac-glass-overlay" />

        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-[101] -translate-x-1/2 -translate-y-1/2',
            SIZE_CLS[size],
            'pac-glass-raised rounded-lg border border-neutral-800 p-6',
            'focus:outline-none',
            className,
          )}
          onInteractOutside={(e) => { if (blockOutside) e.preventDefault() }}
          onEscapeKeyDown={(e) => { if (blockOutside) e.preventDefault() }}
        >
          {/* header */}
          {(title || description) && (
            <div className="mb-4">
              {title && (
                <Dialog.Title className="text-base font-semibold text-neutral-100 tracking-tight">
                  {title}
                </Dialog.Title>
              )}
              {description && (
                <Dialog.Description className="mt-1 text-sm text-neutral-500 leading-relaxed">
                  {description}
                </Dialog.Description>
              )}
            </div>
          )}

          {/* body */}
          <div className="space-y-4">{children}</div>

          {/* footer */}
          <div className="mt-6 flex justify-end gap-3">
            {footer ?? (
              <>
                <Dialog.Close asChild>
                  <button
                    disabled={isBusy}
                    className={clsx(
                      'pac-glass-btn px-3.5 py-1.5 rounded text-sm text-neutral-300',
                      'hover:text-rose-400 hover:border-rose-500',
                      isBusy && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    {cancelLabel}
                  </button>
                </Dialog.Close>

                <button
                  onClick={onConfirm}
                  disabled={confirmDisabled || isBusy}
                  className={clsx(
                    'pac-glass-btn px-3.5 py-1.5 rounded text-sm font-medium',
                    confirmTone === 'danger'
                      ? (confirmDisabled || isBusy
                          ? 'text-neutral-500 cursor-not-allowed'
                          : 'text-neutral-300 hover:text-rose-400 hover:border-rose-500')
                      : (confirmDisabled || isBusy
                          ? 'text-neutral-500 cursor-not-allowed'
                          : 'text-neutral-300 hover:text-emerald-400 hover:border-emerald-500'),
                  )}
                >
                  {isBusy ? 'Working…' : confirmLabel}
                </button>
              </>
            )}
          </div>

          {/* close (X) */}
          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className={clsx(
                'absolute right-3 top-3 p-1 rounded text-neutral-600 hover:text-neutral-300 transition-colors',
                blockOutside && 'pointer-events-none opacity-50',
              )}
              disabled={blockOutside}
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
